<template>
  <el-dialog v-dialog-window="resizeDialogMap" title="规划运输线路" :visible.sync="dialogVisible" width="1180px" top="4vh" append-to-body :close-on-click-modal="false" @open="resetForm">
    <route-form v-if="dialogVisible && initialDraft" :key="sessionId" ref="planner" embedded :initial-form="initialDraft" @draft-change="draft = $event" @confirm="onPlanConfirm" @cancel="dialogVisible = false" />
    <p class="route-save-tip">{{ unchanged ? '原样使用当前已保存线路，无需另存。' : canCreate ? '确认后将自动命名并保存新线路档案，原线路保留。' : '当前账号没有新增线路权限，可原样使用已有线路，或取消并关闭规划后保存运单。' }}</p>
    <span slot="footer">
      <el-button @click="dialogVisible = false">取消</el-button>
      <el-button v-if="unchanged" type="primary" :loading="saving" @click="$refs.planner.submit()">使用此线路</el-button>
      <el-button v-else v-permission="'route:add'" type="primary" :loading="saving" @click="$refs.planner.submit()">保存并使用此线路</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { hasPermission } from '@/utils/auth'
import RouteForm from './RouteForm.vue'
import { isRequestSuccess, saveRoute } from './lingdongApi'
import { normalizeAreaCodes, getAreaName } from './locationArea'

const clone = value => JSON.parse(JSON.stringify(value))
const planNumber = value => value == null || value === '' ? null : Number(value)
const planIdentity = value => JSON.stringify({ nodes: [value.start, ...(value.waypoints || []), value.end].map(node => node && node.lonLat), drivingStrategy: Number(value.drivingStrategy || 0), routeList: value.routeList || '', totalDileage: planNumber(value.totalDileage), totalDuration: planNumber(value.totalDuration), tolls: planNumber(value.tolls), tollDistance: planNumber(value.tollDistance) })
const operationUuid = () => 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, character => {
  const value = Math.floor(Math.random() * 16)
  return (character === 'x' ? value : (value & 3) | 8).toString(16)
})

export default {
  name: 'WaybillRouteDialog',
  components: { RouteForm },
  props: {
    visible: { type: Boolean, default: false },
    initialWaybill: { type: Object, default: () => ({}) },
    initialStopovers: { type: Array, default: () => [] },
    initialRoute: { type: Object, default: null },
    sourceRoute: { type: Object, default: null }
  },
  data() {
    return { dialogVisible: false, saving: false, confirmed: false, initialDraft: null, draft: null, sessionId: 0, saveRequestSequence: 0, operationKey: '', operationIdentity: '' }
  },
  computed: {
    canCreate() { return hasPermission('route:add') },
    unchanged() { return Boolean(this.initialRoute && this.initialRoute.id && this.draft && this.draft.routeList && planIdentity(this.draft) === planIdentity(this.initialDraft)) }
  },
  watch: {
    visible: { immediate: true, handler(value) { this.dialogVisible = value } },
    dialogVisible(value) {
      if (!value) { this.saveRequestSequence++; this.saving = false }
      this.$emit('update:visible', value)
      if (!value && !this.confirmed) this.$emit('cancel')
    }
  },
  beforeDestroy() { this.saveRequestSequence++ },
  methods: {
    resizeDialogMap() { if (this.$refs.planner && this.$refs.planner.map) this.$refs.planner.map.resize() },
    resetForm() {
      this.saveRequestSequence++
      this.saving = false
      this.confirmed = false
      this.sessionId++
      const route = this.initialRoute || {}
      this.initialDraft = clone({
        id: route.id || null, routeName: route.routeName || '', drivingStrategy: Number(route.drivingStrategy || 0),
        start: this.initialWaybill.start || {}, end: this.initialWaybill.target || {}, waypoints: this.initialStopovers,
        routeList: route.routeList || '', totalDileage: route.totalDileage == null ? null : route.totalDileage,
        totalDuration: route.totalDuration == null ? null : route.totalDuration, tolls: route.tolls == null ? null : route.tolls, tollDistance: route.tollDistance == null ? null : route.tollDistance
      })
      this.draft = clone(this.initialDraft)
    },
    convertNode(node) {
      const areaCodes = normalizeAreaCodes(node.areaCodes)
      return { address: node.address || '', lonLat: node.lonLat || '', areaCodes: areaCodes.join(','), areaName: getAreaName(areaCodes) || node.areaName || '', contactName: node.contactName || '', contactPhone: node.contactPhone || '', radius: Number(node.radius) || 500 }
    },
    buildPayload(form) {
      const start = this.convertNode(form.start)
      const end = this.convertNode(form.end)
      return {
        sourceRouteId: this.sourceRoute ? this.sourceRoute.id : null, operationKey: this.operationKey,
        routeName: `${start.address}—${end.address}`.slice(0, 100), status: 1, isDefault: 1,
        startAddress: start.address, startLonlat: start.lonLat, startAreaCodes: start.areaCodes, startAreaName: start.areaName, startRadius: start.radius,
        endAddress: end.address, endLonlat: end.lonLat, endAreaCodes: end.areaCodes, endAreaName: end.areaName, endRadius: end.radius,
        waypoints: JSON.stringify((form.waypoints || []).map(this.convertNode)), drivingStrategy: form.drivingStrategy,
        routeList: form.routeList, totalDileage: form.totalDileage, totalDuration: form.totalDuration, tolls: form.tolls, tollDistance: form.tollDistance
      }
    },
    async onPlanConfirm(form) {
      if (this.saving) return
      this.draft = clone(form)
      if (!form.routeList) { this.$message.warning('请先规划并选择线路'); return }
      if (this.unchanged) {
        this.confirmed = true
        this.$emit('saved', clone(this.initialRoute), clone(form))
        this.dialogVisible = false
        return
      }
      if (!this.canCreate) { this.$message.warning('没有新增线路权限，请原样使用已有线路或关闭规划'); return }
      const payload = this.buildPayload(form)
      delete payload.operationKey
      const identity = JSON.stringify(payload)
      // 只有完整请求内容相同的重试才复用操作号，避免同键对应不同保存内容。
      if (identity !== this.operationIdentity) { this.operationIdentity = identity; this.operationKey = operationUuid() }
      payload.operationKey = this.operationKey
      const requestId = ++this.saveRequestSequence
      this.saving = true
      try {
        const response = await saveRoute(payload)
        if (requestId !== this.saveRequestSequence) return
        if (!isRequestSuccess(response) || !response.data || !response.data.id) throw new Error(response.msg || '线路保存失败')
        this.$message.success('新线路已保存并用于当前运单')
        this.confirmed = true
        this.$emit('saved', response.data, clone(form))
        this.dialogVisible = false
      } catch (error) {
        if (requestId === this.saveRequestSequence) this.$message.error(error.message || '线路保存失败，请重试')
      } finally {
        if (requestId === this.saveRequestSequence) this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.route-save-tip { margin: 12px 0 0; color: #909399; font-size: 13px; }
::v-deep .dialog-window > .el-dialog__body { display: flex; flex-direction: column; overflow: hidden; }
::v-deep .dialog-window .route-planner-page--embedded { height: auto; min-height: 0; flex: 1; }
::v-deep .dialog-window .route-save-tip { flex: none; }
</style>
